import { Theme } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { BindToFields, CssEntry } from '../types/contentfulAttributes'
import { Regex, getRegexMatch } from './regex'

type ClassNameMap<ClassKeys extends string = string> = {
  [K in ClassKeys]: string
}
type BindToFieldMap = { [key in BindToFields]?: keyof ClassNameMap }

interface Maps<ClassKeys extends string = string> {
  classNameMap: ClassNameMap<ClassKeys>
  bindToFieldMap: BindToFieldMap
}

/**
 * createContentfulClassGetter is a utility function that merges all the CSS rules
 * defined for an entry and creates a map of class names and fields they are
 * bound to. Use some conditional `className` string constructor like `clsx`
 * as the returned function may return `undefined`.
 * This currently doesn't support the `props` object passed to `makeStyles`.
 * @param cssEntries An array of `CssEntry` objects.
 * @returns A function that takes a `BindToField` argument that defaults to `Main`
 * when no argument is passed and returns the associated Mui class name.
 */

export const createContentfulClassGetter = (
  cssEntries?: CssEntry[] | null
):
  | ((_: string) => undefined)
  | ((bindToFieldArg?: BindToFields) => string | undefined) => {
  // When we have no CSS entries associated with a particular entry,
  // the returned function returns `undefined`
  if (!cssEntries) {
    return (_: string) => undefined
  }

  const classKeys = cssEntries.map((entry) => entry.className)

  const getThemeKeys = (keyString: string) =>
    keyString
      .split('.')
      // Split the keys like white[100]
      .flatMap((k) => k.split(Regex.textBetweenSquareBrackets))
      // Remove any empty strings
      .filter(Boolean)
      // Remove `theme`
      .slice(1)

  // Create a union type with all `classKeys`
  type ClassKeys = (typeof classKeys)[number]

  // `bindToFieldMap` is a map of `BindToFields` keys and classKeys
  // `classNameMap` is a map of the `classKeys`liand Mui's internal `className` strings
  const { bindToFieldMap, classNameMap }: Maps<ClassKeys> = cssEntries.reduce(
    ({ classNameMap, bindToFieldMap }, cssEntry) => {
      const {
        className,
        cssRules: { cssRules },
        bindToField,
      } = cssEntry

      const { classes: contentfulStyles } = makeStyles()((theme) => ({
        [className]: {
          ...JSON.parse(cssRules, function (key, value: any) {
            if (key.includes('theme')) {
              const dynamicKey = getRegexMatch(
                Regex.textBetweenSquareBrackets,
                key
              )

              const themeKeys = getThemeKeys(dynamicKey)

              const resolvedKey = themeKeys.reduce((acc, val) => {
                // Check if the key is callable
                // For example: The `down` function in `theme.breakpoints.down('md')`
                if (Regex.textBetweenParenthesis.test(val)) {
                  const callable = val.split('(')[0]
                  const arg = getRegexMatch(Regex.textBetweenParenthesis, val)

                  return acc[callable as keyof Theme](arg)
                }

                return acc[val as keyof Theme]
              }, theme) as unknown as string

              // Add the resolved key onto the parsed object and return to get rid of
              // the original string version of the dynamic key
              this[resolvedKey] = value
              return
            }

            // Resolve the dynamic string values
            if (typeof value === 'string' && value.includes('theme')) {
              const themeKeys = getThemeKeys(value)

              const resolvedValue = themeKeys.reduce(
                (acc, val) => acc[val as keyof Theme],
                theme
              )

              return resolvedValue
            }

            return value
          }),
        },
      }))()

      return {
        bindToFieldMap: {
          ...bindToFieldMap,
          [bindToField]: className,
        },
        classNameMap: {
          ...classNameMap,
          ...contentfulStyles,
        },
      }
    },
    {
      bindToFieldMap: {},
      classNameMap: {},
    }
  )

  return (
    bindToFieldArg: BindToFields = BindToFields.Main
  ): string | undefined => {
    const className = bindToFieldMap[bindToFieldArg]

    if (className) {
      return classNameMap[className]
    }

    return undefined
  }
}
